//Skillsuprise CreateCertificate.jsx
// Reworked: pick certificate type -> pick batch -> pick student (auto-fills
// name + purchase id + course) -> pick dates -> Load -> edit the SVG (drag /
// code / add) -> Save as reusable template and/or Issue to the student.

import React, { useContext, useEffect, useState } from "react";
import dateFormat from "dateformat";
import { AuthContext } from "../../context/AuthContext";
import DetectDevice from "../../context/DetectDevice";
import DefaultToast from "../../components/toasts/DefaultToast";
import { useDispatch } from "react-redux";

import GetActiveBatches from "../../actions/course/GetActiveBatches";
import GetBatchStudents from "../../actions/certificate/GetBatchStudents";
import GetCertificateTemplates from "../../actions/certificate/GetCertificateTemplates";
import SaveCertificateTemplate from "../../actions/certificate/SaveCertificateTemplate";
import IssueCertificate from "../../actions/certificate/IssueCertificate";
import DeleteCertificateTemplate from "../../actions/certificate/DeleteCertificateTemplate";

import SvgTemplateEditor from "../../components/svg-editor/SvgTemplateEditor";
import { DEFAULT_CERTIFICATE_SVG } from "./defaultCertificateTemplate";
import { substituteTokens, reverseTokens, svgToPngBlob, getViewBox, downloadBlob } from "../../components/svg-editor/svgUtils";

const CreateCertificate = () => {
  const dispatch = useDispatch();
  const { user } = useContext(AuthContext);
  const device = DetectDevice();

  // form
  const [type, setType] = useState("training");
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [name, setName] = useState("");
  const [certificateId, setCertificateId] = useState("");
  const [courseUrl, setCourseUrl] = useState("");
  const [courseTitle, setCourseTitle] = useState("");
  const [userId, setUserId] = useState("");

  const [startdate, setStartDate] = useState("");
  const [enddate, setEndDate] = useState("");
  const [issueddate, setIssuedDate] = useState("");

  // templates / editor
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("default");
  const [svg, setSvg] = useState("");
  const [loaded, setLoaded] = useState(false);

  const [savingTemplate, setSavingTemplate] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [issuedImage, setIssuedImage] = useState(null);

  const [toast, setToast] = useState(false);
  const [toastData, setToastData] = useState(null);

  const notify = (title, message) => {
    setToastData({ title, message });
    setToast(true);
  };

  // ---- load batches + templates ------------------------------------
  const loadBatches = async () => {
    try {
      const res = await GetActiveBatches({ payload: { session: user.SessionId, status: "active", limit: 500 } });
      if (res && res.status === "success") setBatches(res.data || []);
    } catch (e) {
      /* batches stay empty; admin can still type values manually */
    }
  };

  const loadTemplates = async (forType) => {
    try {
      const res = await GetCertificateTemplates({ payload: { type: forType, session: user.SessionId } });
      if (res && res.status === "success") setTemplates(res.templates || []);
      else setTemplates([]);
    } catch (e) {
      setTemplates([]);
    }
  };

  useEffect(() => {
    if (user && user.SessionId) loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTemplates(type);
    setSelectedTemplateId("default");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // ---- batch -> students -------------------------------------------
  const onSelectBatch = async (batchId) => {
    const batch = batches.find((b) => String(b.batch_id) === String(batchId)) || null;
    setSelectedBatch(batch);
    setSelectedStudent(null);
    setStudents([]);
    if (!batch) return;

    // Primary: dedicated endpoint (gives user_id + course_title). Fallback:
    // the students already embedded in the batch (works before backend deploy).
    let list = [];
    try {
      const res = await GetBatchStudents({
        payload: { session: user.SessionId, batch_id: batch.batch_id, course_url: batch.course },
      });
      if (res && res.status === "success" && Array.isArray(res.students) && res.students.length) {
        list = res.students;
      }
    } catch (e) {
      /* fall through to embedded students */
    }
    if (!list.length && Array.isArray(batch.students)) {
      list = batch.students.map((s) => ({
        ...s,
        course_url: s.course_url || batch.course,
      }));
    }
    setStudents(list);
  };

  const onSelectStudent = (purchaseId) => {
    const s = students.find((x) => String(x.purchase_id) === String(purchaseId)) || null;
    setSelectedStudent(s);
    if (!s) return;
    setName(((s.first_name || "") + " " + (s.last_name || "")).trim());
    setCertificateId(String(s.purchase_id || ""));
    setCourseUrl(s.course_url || (selectedBatch && selectedBatch.course) || "");
    setCourseTitle(s.course_title || (selectedBatch && selectedBatch.course) || "");
    setUserId(s.user_id || "");
  };

  // ---- token data + date helpers -----------------------------------
  const fmt = (d) => (d ? dateFormat(new Date(d), "dS mmmm yyyy") : "");
  const issuedForDb = () => {
    if (!issueddate) return dateFormat(new Date(), "yyyy-mm-dd HH:MM:ss");
    const d = new Date(issueddate);
    const now = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`;
  };
  // What gets stored in the DB `type` column — branded, never "skp-*".
  const storedType = (t) => {
    const map = {
      training: "siliconminds-training",
      workshop: "siliconminds-workshop",
      "skp-internship": "siliconminds-internship",
      internship: "siliconminds-internship",
      appreciation: "siliconminds-appreciation",
    };
    return map[t] || "siliconminds-" + String(t || "certificate").replace(/^skp-/, "");
  };

  const buildData = () => ({
    name: name || "",
    certificate_id: certificateId || "",
    course: courseTitle || courseUrl || "",
    start_date: fmt(startdate),
    end_date: fmt(enddate),
    issued_date: fmt(issueddate),
  });

  const activeTemplateSvg = (id = selectedTemplateId) => {
    if (id === "default") return DEFAULT_CERTIFICATE_SVG;
    const t = templates.find((x) => String(x.id) === String(id));
    return (t && t.svg) || DEFAULT_CERTIFICATE_SVG;
  };

  // selecting a template instantly re-renders the loaded preview
  const selectTemplate = (id) => {
    setSelectedTemplateId(id);
    if (loaded && name) {
      setSvg(substituteTokens(activeTemplateSvg(id), buildData()));
      setIssuedImage(null);
    }
  };

  const deleteTemplate = async (id, tplName) => {
    if (!window.confirm(`Delete template "${tplName}"?`)) return;
    try {
      const res = await DeleteCertificateTemplate({ payload: { session: user.SessionId, id } });
      if (res && res.status === "success") {
        if (String(selectedTemplateId) === String(id)) selectTemplate("default");
        loadTemplates(type);
        notify("Deleted", "Template removed.");
      } else {
        notify("Error", (res && res.message) || "Could not delete template.");
      }
    } catch (e) {
      notify("Error", e.message);
    }
  };

  // ---- actions ------------------------------------------------------
  const loadCertificate = () => {
    if (!name) {
      notify("Missing info", "Select a student (or enter a name) first.");
      return;
    }
    setSvg(substituteTokens(activeTemplateSvg(), buildData()));
    setIssuedImage(null);
    setLoaded(true);
  };

  const saveAsTemplate = async () => {
    if (!svg) return;
    const tplName = window.prompt("Template name (to reuse later):", `${type} certificate`);
    if (!tplName) return;
    setSavingTemplate(true);
    try {
      const vb = getViewBox(svg);
      const tokenSvg = reverseTokens(svg, buildData());
      const res = await SaveCertificateTemplate({
        payload: { id: 0, name: tplName, type, svg: tokenSvg, width: vb.width, height: vb.height, session: user.SessionId },
      });
      if (res && res.status === "success") {
        notify("Saved", "Template saved for future use.");
        loadTemplates(type);
      } else {
        notify("Error", (res && res.message) || "Could not save template.");
      }
    } catch (e) {
      notify("Error", e.message);
    }
    setSavingTemplate(false);
  };

  const issueCertificate = async () => {
    if (!svg) return;
    if (!certificateId && !selectedStudent) {
      notify("Missing info", "Select a student before issuing.");
      return;
    }
    setIssuing(true);
    dispatch({ type: "LOADING" });
    try {
      const vb = getViewBox(svg);
      let pngBlob = null;
      try {
        pngBlob = await svgToPngBlob(svg, vb.width, vb.height, 3);
      } catch (e) {
        // Issue anyway with the SVG; PNG can be regenerated from svg later.
        console.warn("PNG export failed, issuing with SVG only:", e.message);
      }
      const res = await IssueCertificate({
        payload: {
          session: user.SessionId,
          purchase_id: certificateId,
          user_id: userId,
          certificate_id: certificateId,
          name,
          gender: selectedStudent ? selectedStudent.gender : "",
          course_url: courseUrl,
          course_title: courseTitle,
          type: storedType(type),
          template_id: selectedTemplateId === "default" ? 0 : selectedTemplateId,
          svg,
          // raw YYYY-MM-DD for the datetime columns; the SVG already carries
          // the human-readable dates substituted at load time.
          start_date: startdate,
          end_date: enddate,
          issued_date: issuedForDb(),
          pngBlob,
        },
      });
      dispatch({ type: "DONE_LOADING" });
      if (res && res.status === "success") {
        setIssuedImage(res.image_url || null);
        notify("Success", "Certificate issued to the student.");
      } else {
        notify("Error", (res && res.message) || "Could not issue certificate.");
      }
    } catch (e) {
      dispatch({ type: "DONE_LOADING" });
      notify("Error", e.message);
    }
    setIssuing(false);
  };

  const downloadPng = async () => {
    if (!svg) return;
    try {
      const vb = getViewBox(svg);
      const blob = await svgToPngBlob(svg, vb.width, vb.height, 3);
      downloadBlob(blob, `certificate_${certificateId || "preview"}.png`);
    } catch (e) {
      notify("Error", "Could not export PNG: " + e.message);
    }
  };

  const batchLabel = (b) =>
    b.batch_name ||
    `${b.course || "course"} • ${b.class_starts || ""}${b.batch_id ? " (#" + b.batch_id + ")" : ""}`;

  // ---- design tokens (simple & clean) ------------------------------
  const C = { ink: "#1f2937", blue: "#2563eb", muted: "#8a909c", border: "#e6e8ec" };
  const label = { fontSize: 12.5, fontWeight: 600, color: "#5b616e", marginBottom: 6, display: "block" };
  const field = { borderRadius: 8, border: "1px solid #d9dde3", padding: "10px 12px", fontSize: 14, width: "100%", background: "#fff", color: "#1f2937" };
  const card = { background: "#fff", borderRadius: 12, border: "1px solid #e8eaed", boxShadow: "0 1px 2px rgba(16,24,40,.04)" };
  const tplRow = (active) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
    border: `1px solid ${active ? "#2563eb" : "#e3e6eb"}`,
    background: active ? "#eef4ff" : "#fff",
    borderRadius: 8, padding: "9px 12px", cursor: "pointer",
  });
  const delBtn = { border: "none", background: "transparent", color: "#b42318", cursor: "pointer", fontSize: 13, padding: "2px 8px", borderRadius: 6, lineHeight: 1, whiteSpace: "nowrap" };
  const chip = (n) => ({
    width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    background: "#eef1f5", color: C.ink, fontSize: 12, fontWeight: 700,
  });
  const awardIcon = (size, color) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="6" /><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
    </svg>
  );
  // helper (called, not used as <Section/>, so inputs keep focus on re-render)
  const section = (n, title, sub, content) => (
    <div style={{ marginBottom: 22 }}>
      <div className="d-flex align-items-center" style={{ gap: 10, marginBottom: 12 }}>
        <span style={chip(n)}>{n}</span>
        <div>
          <div style={{ fontWeight: 700, color: C.ink, fontSize: 14 }}>{title}</div>
          {sub ? <div style={{ fontSize: 11.5, color: C.muted }}>{sub}</div> : null}
        </div>
      </div>
      {content}
    </div>
  );

  return (
    <>
      <DefaultToast show={toast} toastdata={toastData} ToastHandleClose={() => setToast(false)} />
      <style>{`
        .cc-input{outline:none;}
        .cc-input:focus{border-color:${C.blue} !important;box-shadow:0 0 0 3px rgba(37,99,235,.12);}
        .cc-btn{transition:background .15s ease, border-color .15s ease, opacity .15s ease;}
        .cc-btn:hover:not(:disabled){opacity:.92;}
        .cc-btn:disabled{opacity:.55;cursor:not-allowed;}
      `}</style>

      <div style={{ background: "#f7f8fa", minHeight: "100vh", padding: device === "mobile" ? "16px 12px" : "28px 24px" }}>
        {/* simple header */}
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ margin: 0, fontWeight: 700, color: C.ink }}>Create Certificate</h4>
          <p style={{ margin: "4px 0 0", fontSize: 13.5, color: C.muted }}>Generate, design and issue certificates to your students.</p>
        </div>

        <div className="row g-4">
          {/* LEFT: preview / editor */}
          <div className="col-12 col-lg-7">
            <div style={{ ...card, padding: 20 }}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 style={{ margin: 0, fontWeight: 700, color: C.ink }}>Preview &amp; Editor</h6>
                {loaded ? <span style={{ fontSize: 12, color: C.muted }}>Drag · edit · code</span> : null}
              </div>

              {loaded ? (
                <>
                  <SvgTemplateEditor value={svg} onChange={setSvg} hint="Drag any text to reposition • double-click text to edit • use the code box for full control" />
                  <div className="d-flex flex-wrap align-items-center mt-3" style={{ gap: 10 }}>
                    <button className="cc-btn" onClick={saveAsTemplate} disabled={savingTemplate} style={{ border: "1px solid #d9dde3", color: "#374151", background: "#fff", borderRadius: 8, padding: "9px 14px", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                      {savingTemplate ? "Saving…" : "Save as Template"}
                    </button>
                    <button className="cc-btn" onClick={downloadPng} style={{ border: "1px solid #d9dde3", color: "#374151", background: "#fff", borderRadius: 8, padding: "9px 14px", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                      Download PNG
                    </button>
                    <button className="cc-btn" onClick={issueCertificate} disabled={issuing} style={{ marginLeft: "auto", border: "none", color: "#fff", background: C.blue, borderRadius: 8, padding: "9px 18px", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                      {issuing ? "Issuing…" : "Issue to Student"}
                    </button>
                  </div>
                  {issuedImage && (
                    <div className="mt-3" style={{ border: "1px solid #e6e8ec", background: "#fafbfc", borderRadius: 10, padding: 14 }}>
                      <p style={{ margin: "0 0 8px", fontWeight: 600, color: "#16a34a", fontSize: 13.5 }}>Issued — stored certificate</p>
                      <img src={issuedImage} alt="issued certificate" style={{ maxWidth: "100%", borderRadius: 6, border: "1px solid #eee" }} />
                    </div>
                  )}
                </>
              ) : (
                <div style={{ border: "1px dashed #d8dce3", borderRadius: 10, background: "#fafbfc", minHeight: 320, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 24 }}>
                  {awardIcon(28, "#aab1bd")}
                  <p style={{ fontWeight: 600, color: C.ink, margin: "12px 0 2px" }}>No preview yet</p>
                  <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>Fill the form and click <b>Load Certificate</b>.</p>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: form */}
          <div className="col-12 col-lg-5">
            <div style={{ ...card, padding: 20 }}>
              {section(1, "Certificate Type", null, (
                <select className="cc-input" style={field} value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="training">Training Certificate</option>
                  <option value="workshop">Workshop Certificate</option>
                  <option value="skp-internship">Internship Certificate</option>
                  <option value="appreciation">Appreciation Certificate</option>
                </select>
              ))}

              {section(2, "Recipient", "Pick the batch, then the student — details auto-fill", (
                <>
                  <label style={label}>Batch</label>
                  <select className="cc-input" style={field} value={selectedBatch ? selectedBatch.batch_id : ""} onChange={(e) => onSelectBatch(e.target.value)}>
                    <option value="">— Select batch —</option>
                    {batches.map((b) => (<option key={b.batch_id} value={b.batch_id}>{batchLabel(b)}</option>))}
                  </select>
                  <label style={{ ...label, marginTop: 14 }}>Student</label>
                  <select className="cc-input" style={field} value={selectedStudent ? selectedStudent.purchase_id : ""} onChange={(e) => onSelectStudent(e.target.value)} disabled={!students.length}>
                    <option value="">{students.length ? "— Select student —" : "Select a batch first"}</option>
                    {students.map((s) => (
                      <option key={s.purchase_id} value={s.purchase_id}>
                        {(s.first_name || "") + " " + (s.last_name || "")}{s.purchase_id ? ` (#${s.purchase_id})` : ""}
                      </option>
                    ))}
                  </select>
                </>
              ))}

              {section(3, "Certificate Details", null, (
                <>
                  <label style={label}>Full Name</label>
                  <input className="cc-input" style={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="Auto-filled from student" />
                  <label style={{ ...label, marginTop: 14 }}>Certificate ID</label>
                  <input className="cc-input" style={field} value={certificateId} onChange={(e) => setCertificateId(e.target.value)} placeholder="Auto-filled (purchase id)" />
                  <label style={{ ...label, marginTop: 14 }}>Course Title</label>
                  <input className="cc-input" style={field} value={courseTitle} onChange={(e) => setCourseTitle(e.target.value)} placeholder="Auto-filled from student" />
                </>
              ))}

              {section(4, "Validity Dates", null, (
                <>
                  <div className="d-flex" style={{ gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={label}>Start</label>
                      <input className="cc-input" style={field} type="date" value={startdate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={label}>End</label>
                      <input className="cc-input" style={field} type="date" value={enddate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                  </div>
                  <label style={{ ...label, marginTop: 14 }}>Issued Date</label>
                  <input className="cc-input" style={field} type="date" value={issueddate} onChange={(e) => setIssuedDate(e.target.value)} />
                </>
              ))}

              {section(5, "Template", "Switching a template instantly updates the preview", (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div onClick={() => selectTemplate("default")} style={tplRow(selectedTemplateId === "default")}>
                    <span style={{ fontSize: 14, color: C.ink, fontWeight: selectedTemplateId === "default" ? 600 : 500 }}>Default design</span>
                  </div>
                  {templates.map((t) => {
                    const active = String(selectedTemplateId) === String(t.id);
                    return (
                      <div key={t.id} onClick={() => selectTemplate(t.id)} style={tplRow(active)}>
                        <span style={{ fontSize: 14, color: C.ink, fontWeight: active ? 600 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</span>
                        <button title="Delete template" onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id, t.name); }} style={delBtn}>🗑 Delete</button>
                      </div>
                    );
                  })}
                  {templates.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: C.muted }}>No saved templates yet — save one from the editor.</div>
                  ) : null}
                </div>
              ))}

              <button className="cc-btn" onClick={loadCertificate} style={{ width: "100%", border: "none", color: "#fff", background: C.blue, borderRadius: 8, padding: "12px", fontSize: 14.5, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>
                Load Certificate
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateCertificate;
